import type { ProjectListItem } from '@nightshift/ui-types';

export interface CreateTicketDraft {
  project: string;
  title: string;
  problem: string;
  acceptanceCriteria: string;
  milestoneId: number | null;
  /** Empty / Auto means the refiner picks. */
  verificationProfile: string;
}

export function createTicketFormError(
  draft: Pick<CreateTicketDraft, 'project' | 'title'>,
  projects: readonly ProjectListItem[] | null,
): string | null {
  if (projects !== null && projects.length === 0) return 'No projects registered.';
  if (draft.title.trim().length === 0) return 'Title is required.';
  if (projects !== null && draft.project.trim().length === 0) return 'No projects registered.';
  return null;
}

export function namedMilestoneError(name: string): string | null {
  if (name.trim().length === 0) return 'Milestone name is required.';
  return null;
}

export function createTicketBody(draft: CreateTicketDraft): {
  project: string;
  title: string;
  problem?: string;
  acceptance_criteria?: string;
  milestone_id?: number;
  verificationProfile?: string;
} {
  const profile = draft.verificationProfile.trim();
  return {
    project: draft.project,
    title: draft.title.trim(),
    ...(draft.problem.trim() ? { problem: draft.problem.trim() } : {}),
    ...(draft.acceptanceCriteria.trim() ? { acceptance_criteria: draft.acceptanceCriteria.trim() } : {}),
    ...(draft.milestoneId != null ? { milestone_id: draft.milestoneId } : {}),
    ...(profile && profile.toLowerCase() !== 'auto' ? { verificationProfile: profile } : {}),
  };
}

/** What a generated draft may set. Mirrors the server's `TicketDraftResult`. */
export interface GeneratedTicketDraft {
  title: string;
  problem: string | null;
  acceptanceCriteria: string | null;
  milestoneId: number | null;
  verificationProfile: string | null;
}

/**
 * Fold a generated draft into the form state the manual path already uses.
 *
 * Two rules, both from the acceptance criteria. A field the source did not support stays
 * unset rather than being filled with something plausible, so a null arrives as the empty
 * string the manual form starts with. And a constrained choice is only applied when it is
 * still one of the options this form is showing -- a milestone that vanished, or one from a
 * project the user has since switched away from, is dropped rather than carried across.
 *
 * The result is ordinary form state: every field stays editable, and nothing here saves.
 */
export function applyGeneratedDraft(
  current: CreateTicketDraft,
  generated: GeneratedTicketDraft,
  options: { milestoneIds: readonly number[]; profileNames: readonly string[] },
): CreateTicketDraft {
  const milestoneId =
    generated.milestoneId != null && options.milestoneIds.includes(generated.milestoneId)
      ? generated.milestoneId
      : null;
  const profile =
    generated.verificationProfile !== null && options.profileNames.includes(generated.verificationProfile)
      ? generated.verificationProfile
      : '';
  return {
    ...current,
    title: generated.title.trim(),
    problem: generated.problem ?? '',
    acceptanceCriteria: generated.acceptanceCriteria ?? '',
    milestoneId,
    verificationProfile: profile,
  };
}

/** Upload markers must never reach the draft generator or ticket creation. */
export function hasPendingImages(source: string): boolean {
  return /!\[[^\n]*?\]\(uploading-\d+\)/.test(source);
}

/** Keep uploaded images even when the model omits them from its answer. */
export function preserveSourceImages(generated: GeneratedTicketDraft, source: string): GeneratedTicketDraft {
  const images = [...source.matchAll(/!\[(?:\\.|[^\]\n])*\]\((\/api\/attachments\/[^\s)]+)\)/g)];
  const fields = `${generated.problem ?? ''}\n${generated.acceptanceCriteria ?? ''}`;
  const retained = new Set([...fields.matchAll(/!\[(?:\\.|[^\]\n])*\]\((\/api\/attachments\/[^\s)]+)\)/g)].map((match) => match[1]));
  const missing: string[] = [];
  for (const match of images) {
    if (retained.has(match[1])) continue;
    retained.add(match[1]);
    missing.push(match[0]);
  }
  return missing.length ? { ...generated, problem: [generated.problem, ...missing].filter(Boolean).join('\n\n') } : generated;
}
